import React, { useEffect, useState } from "react";
import { Modal, Button, Icon, Message, Dropdown, Form } from "semantic-ui-react";
import "../mdl-delete-critical-unit/mdl-delete-critical-unit.css";
import "./mdl-send-followup.css";

const ALL_SCHEDULERS_VALUE = "__all_schedulers__";

const MDL_SEND_FOLLOWUP = ({
  open,
  onClose,
  onConfirm,
  schedulers = [],
  defaultSchedulerIds = [],
  criticalType,
  loading,
  errorMessage,
}) => {
  const [selectedSchedulerIds, setSelectedSchedulerIds] = useState([]);
  const [selectionError, setSelectionError] = useState("");

  const criticalTypeLabel =
    criticalType === "supervisor" ? "Supervisor" : "Ongoing";

  const schedulerOptions = (schedulers || [])
    .filter((scheduler) => scheduler?.value && scheduler.value !== "all")
    .map((scheduler) => ({
      key: scheduler.value,
      value: scheduler.value,
      text: scheduler.email
        ? `${scheduler.text} (${scheduler.email})`
        : scheduler.text,
    }));

  const allSchedulerIds = schedulerOptions.map((option) => option.value);
  const isAllSelected =
    allSchedulerIds.length > 0 &&
    selectedSchedulerIds.length === allSchedulerIds.length;

  const dropdownOptions = [
    {
      key: ALL_SCHEDULERS_VALUE,
      value: ALL_SCHEDULERS_VALUE,
      text: "All",
    },
    ...schedulerOptions,
  ];

  const dropdownValue = isAllSelected
    ? [ALL_SCHEDULERS_VALUE]
    : selectedSchedulerIds;

  useEffect(() => {
    if (!open) {
      return;
    }

    const availableIds = schedulerOptions.map((option) => option.value);
    const defaults = (defaultSchedulerIds || []).filter((id) =>
      availableIds.includes(id),
    );
    setSelectedSchedulerIds(defaults);
    setSelectionError("");
  }, [open]);

  function handleSchedulerChange(e, { value }) {
    const next = value || [];
    const withoutAll = next.filter((id) => id !== ALL_SCHEDULERS_VALUE);

    if (isAllSelected) {
      // Currently showing "All" — removing the All chip clears selection.
      if (!next.includes(ALL_SCHEDULERS_VALUE)) {
        setSelectedSchedulerIds([]);
      } else if (withoutAll.length) {
        // User picked a specific scheduler while All was selected.
        setSelectedSchedulerIds(withoutAll);
      } else {
        setSelectedSchedulerIds(allSchedulerIds);
      }
    } else if (next.includes(ALL_SCHEDULERS_VALUE)) {
      setSelectedSchedulerIds(allSchedulerIds);
    } else if (withoutAll.length === allSchedulerIds.length) {
      setSelectedSchedulerIds(allSchedulerIds);
    } else {
      setSelectedSchedulerIds(withoutAll);
    }

    if (selectionError) {
      setSelectionError("");
    }
  }

  function handleSelectAll() {
    setSelectedSchedulerIds(allSchedulerIds);
    setSelectionError("");
  }

  function handleClearAll() {
    setSelectedSchedulerIds([]);
  }

  function handleConfirm() {
    if (!selectedSchedulerIds.length) {
      setSelectionError("Please select at least one scheduler.");
      return;
    }
    onConfirm(selectedSchedulerIds);
  }

  const selectedCount = selectedSchedulerIds.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      dimmer="inverted"
      closeOnDimmerClick={!loading}
      className="critical-followup-select-modal"
    >
      <div className="critical-list-modal-header">
        <div className="modal-header-title">Email Site Follow-up</div>
        <div
          className="modal-header-close"
          onClick={loading ? undefined : onClose}
          style={loading ? { pointerEvents: "none", opacity: 0.5 } : undefined}
        >
          <Icon name="times circle outline" />
        </div>
      </div>

      {errorMessage ? (
        <div className="critical-followup-modal-error">
          <Message negative icon size="small">
            <Icon name="warning circle" />
            <Message.Content>
              <Message.Header>Unable to send follow-up emails</Message.Header>
              <p className="critical-followup-modal-error-text">
                {errorMessage}
              </p>
            </Message.Content>
          </Message>
        </div>
      ) : null}

      <div className="critical-list-modal-content">
        <div className="delete-reason-message">
          Select one or more schedulers to send critical unit follow-up emails
          for their <strong>{criticalTypeLabel}</strong> locations.
        </div>
        <p style={{ margin: "0 0 16px", lineHeight: 1.5, color: "#666666" }}>
          One email will be sent per building to site staff (PM/CM), including
          all ongoing/supervisor critical units at that location.
        </p>

        <Form>
          <Form.Field required error={!!selectionError}>
            <label>Schedulers</label>
            <div className="critical-followup-scheduler-actions">
              <button
                type="button"
                className="critical-followup-link-btn"
                onClick={handleSelectAll}
                disabled={loading || !schedulerOptions.length}
              >
                Select all
              </button>
              <span className="critical-followup-link-sep">|</span>
              <button
                type="button"
                className="critical-followup-link-btn"
                onClick={handleClearAll}
                disabled={loading || !selectedCount}
              >
                Clear
              </button>
            </div>
            <Dropdown
              id="followup-scheduler-dropdown"
              className={
                isAllSelected ? "critical-followup-dropdown-all" : undefined
              }
              placeholder="Select scheduler(s)"
              fluid
              multiple
              search
              selection
              selectOnBlur={false}
              options={dropdownOptions}
              value={dropdownValue}
              onChange={handleSchedulerChange}
              disabled={loading || !schedulerOptions.length}
            />
            {selectionError ? (
              <div className="critical-followup-field-error">{selectionError}</div>
            ) : (
              <div className="critical-followup-field-hint">
                {selectedCount === 0
                  ? "No schedulers selected"
                  : isAllSelected
                    ? "All schedulers selected"
                    : `${selectedCount} scheduler${selectedCount === 1 ? "" : "s"} selected`}
              </div>
            )}
          </Form.Field>
        </Form>
      </div>

      <div className="modal-footer">
        <Button basic color="grey" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="orange"
          className="critical-followup-modal-btn"
          onClick={handleConfirm}
          loading={loading}
          disabled={loading || !schedulerOptions.length}
        >
          <img
            src="/follow_up_request.svg"
            alt=""
            className="critical-followup-modal-btn-icon"
          />
          Send
        </Button>
      </div>
    </Modal>
  );
};

export default MDL_SEND_FOLLOWUP;
